import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import { moversPurlPaymentAuthID } from "../../../../shared/hooks/useFetchPayment";
import { encryptNativeSpeedPay } from "../../Utils/utils";
import { publicKeySpeedPay } from "../../../../shared/utils/Constants";
import API_URLS from "../../Utils/axr";
import store from "../store";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import DwLogutil from "../../Utils/DwLogutil";
import { paymentCardIdentification } from "../../../../shared/hooks/useFetchServiceDetails";

type PaymentFormValues = {
  cardNumber: string;
  securityCode: string;
  expirationDate: string;
  agreeTerms?: boolean;
};

type PurlPaymentState = {
  purlPaymentData: any;
  purlPaymentLoading: boolean;
  purlPaymentError: any;
  provisionalBan: string | null;
  paymentAuthId: string | null;
  paymentCardIdentificationData: any;
  paymentCardIdentificationLoading: boolean;
  paymentCardIdentificationError: any;
  normalizedCardNumber: string | null;
};

const initialState: PurlPaymentState = {
  purlPaymentData: null,
  purlPaymentLoading: false,
  purlPaymentError: null,
  provisionalBan: null,
  paymentAuthId: null,
  paymentCardIdentificationData: null,
  paymentCardIdentificationLoading: false,
  paymentCardIdentificationError: null,
  normalizedCardNumber: null,
};

const getEncryptValue = async (text: string) => {
  const encryptData = await encryptNativeSpeedPay(text, publicKeySpeedPay);
  return encryptData;
};

export const identifyPaymentCard = createAsyncThunk(
  "purlPayment/identifyPaymentCard",
  async (formValues: PaymentFormValues, { rejectWithValue }) => {
    try {
      const reduxStore: any = store.getState();

      const headers = reduxStore?.brightspeedToken?.headers;
      const addressQualification =
        reduxStore?.addressQualification?.moversData?.moversAddressQualification ||
        reduxStore?.addressQualification?.data ||
        reduxStore?.addressQualification;

      const normalizedCardNumber = formValues.cardNumber.replace(/\D/g, "");

      const transactionId =
        addressQualification?.entity?.partnerSessionId ||
        uuidv4().slice(0, 21);

      const payload = {
        baseline: {
          transactionId,
          partnerId: "",
          salesCode: "",
          partnerReferenceId: "",
          agentId: "",
          channel: "web-acm",
          siteId: headers?.siteId?.toLowerCase() || "",
          wirecenter: addressQualification?.entity?.wireCenter || "MANTNCXA",
          intent: "",
          workflow: "mover",
          billingType: "postpaid",
        },
        binNumber: normalizedCardNumber.slice(0, 6) || "",
      };

      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_card_identification_triggered})

      let url = API_URLS?.paymentCardIdentification;
      const params = {
        "": [url],
      };
      const response = await paymentCardIdentification(params, payload, headers);

      const data = response.data;

      if (!data) {
        DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_card_identification_failed})
        return rejectWithValue(data);
      }

      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_card_identification_success})

      return data;
    } catch (error: any) {
      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_card_identification_failed})
      return rejectWithValue(
        error?.message || "Payment card identification request failed"
      );
    }
  }
);

export const submitPurlPayment = createAsyncThunk(
  "purlPayment/submitPurlPayment",
  async (formValues: PaymentFormValues, { rejectWithValue, dispatch }) => {
    try {
      const reduxStore: any = store.getState();

      const headers = reduxStore?.brightspeedToken?.headers;
      const provisionalBan = reduxStore?.purlPayment?.provisionalBan;
      const user = reduxStore?.user?.data || reduxStore?.user;

      const addressQualification =
        reduxStore?.addressQualification?.moversData?.moversAddressQualification ||
        reduxStore?.addressQualification?.data ||
        reduxStore?.addressQualification;

      const selectedMoversAddress =
        reduxStore?.addressQualification?.selectedMoversAddress ||
        reduxStore?.moversSelectedAddress?.data ||
        reduxStore?.moversSelectedAddress;

      const moversAddressQualificationData =
        reduxStore?.addressQualification?.moversData?.moversAddressQualification?.entity ||
        reduxStore?.moversAddressQualification?.moversAddressQualificationData ||
        reduxStore?.moversAddressQualification?.data ||
        reduxStore?.moversAddressQualification;

      const normalizedCardNumber = formValues.cardNumber.replace(/\D/g, "");
      const normalizedSecurityCode = formValues.securityCode.replace(/\D/g, "");
      const normalizedExpirationDate = formValues.expirationDate.trim();

      const [expirationMonth = "", expirationYear = ""] =
        normalizedExpirationDate.split("/");

      const transactionId =
        addressQualification?.entity?.partnerSessionId ||
        uuidv4().slice(0, 21);

      const cardTypeResponse: any = await dispatch(
        identifyPaymentCard(formValues)
      ).unwrap();

      const payload = {
        baseline: {
          transactionId,
          partnerId: "",
          salesCode: "",
          partnerReferenceId: "",
          agentId: "",
          channel: "web-acm",
          siteId: moversAddressQualificationData?.siteId || "",
          wirecenter: moversAddressQualificationData?.wireCenter || "",
          intent: "",
          workflow: "mover",
          billingType: "postpaid",
        },
        street: selectedMoversAddress?.street_line || "",
        city: selectedMoversAddress?.city || "",
        state: selectedMoversAddress?.state || "",
        zip:
          selectedMoversAddress?.zipcode ||
          selectedMoversAddress?.zipCode ||
          "",
        cardType: cardTypeResponse?.cardType || "",
        encFields:
          "#field_creditCardNumber#field_cardSecurityCode#field_creditCardExpirationMonth#field_creditCardExpirationYear",
        cardHolderName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
        paymentType: "Card",
        paymentMethodKind: "creditcard",
        encValues: await getEncryptValue(
          `#${normalizedCardNumber}#${normalizedSecurityCode}#${expirationMonth}#${expirationYear}`
        ),
        pageIndicator: "autopay",
        host: window.location.hostname,
        autopay: "true",
        transactionId,
        autoPayCardType: "Debit",
        mrcCardType: "",
        classValue: normalizedCardNumber.slice(0, 6) || "",
        creditAccount: "",
        provisionalBan: provisionalBan || "",
      };
      let url = API_URLS?.moversPurlPaymentAuthID;
      const params = {
        "": [url],
      };
      const response = await moversPurlPaymentAuthID(
        params,
        payload,
        headers
      );

      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_auth_id_triggered});
      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_attempted});

      const data = response.data;

      if (!data) {
        DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_auth_id_failed})
        DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_failed});
        return rejectWithValue(data);
      }

      return data;
    } catch (error: any) {
      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_auth_id_failed})
      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_payment_failed});
      return rejectWithValue(error?.message || "PURL payment request failed");
    }
  }
);

const purlPaymentSlice = createSlice({
  name: "purlPayment",
  initialState,
  reducers: {
    clearPurlPaymentState: () => initialState,
    setProvisionalBan: (state, action: PayloadAction<string | null>) => {
      state.provisionalBan = action.payload;
    },
    setPaymentAuthId: (state, action: PayloadAction<string | null>) => {
      state.paymentAuthId = action.payload;
    },
    setNormalizedCardNumber: (state, action: PayloadAction<string | null>) => {
      state.normalizedCardNumber = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(identifyPaymentCard.pending, (state) => {
        state.paymentCardIdentificationLoading = true;
        state.paymentCardIdentificationError = null;
      })
      .addCase(
        identifyPaymentCard.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.paymentCardIdentificationLoading = false;
          state.paymentCardIdentificationData = action.payload;
          state.paymentCardIdentificationError = null;
        }
      )
      .addCase(identifyPaymentCard.rejected, (state, action) => {
        state.paymentCardIdentificationLoading = false;
        state.paymentCardIdentificationData = null;
        state.paymentCardIdentificationError =
          action.payload || action.error?.message;
      })
      .addCase(submitPurlPayment.pending, (state) => {
        state.purlPaymentLoading = true;
        state.purlPaymentError = null;
      })
      .addCase(
        submitPurlPayment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.purlPaymentLoading = false;
          state.purlPaymentData = action.payload;
          state.purlPaymentError = null;
          state.provisionalBan = action.payload?.provisionalBan || null;
          state.paymentAuthId = action.payload?.paymentAuthId || null;
        }
      )
      .addCase(submitPurlPayment.rejected, (state, action) => {
        state.purlPaymentLoading = false;
        state.purlPaymentData = null;
        state.purlPaymentError = action.payload || action.error?.message;
      });
  },
});

export const {
  clearPurlPaymentState,
  setProvisionalBan,
  setPaymentAuthId,
  setNormalizedCardNumber,
} = purlPaymentSlice.actions;

export default purlPaymentSlice.reducer;